import { useState, useRef, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { RefreshCwIcon, ExternalLinkIcon, ActivityIcon, Loader2Icon, CheckIcon, XIcon, AlertTriangleIcon, EyeIcon, CodeXmlIcon, BadgeCheckIcon } from "lucide-react";
import { useIntl } from "react-intl";

import Message from "../../../../i18n/Message";
import PreviewContent from "./PreviewContent";
import SourcesViewer from "./sources/SourcesViewer";
import ProjectEventsOverlay from "./sources/ProjectEventsOverlay";
import PublishContent from "./publish/PublishContent";
import { loadSourcesTree } from "../../store/builderActionCreators";
import { builderSelectors } from "../../store/builderReducer";

import "./PreviewPanel.scss";

const TABS = {
  PREVIEW: "preview",
  SOURCES: "sources",
  PUBLISH: "publish",
};

const PreviewPanel = ({ project, agentSession, isAgentRunning }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { workspaceId } = useParams();
  const [activeTab, setActiveTab] = useState(TABS.PREVIEW);
  const [currentPath, setCurrentPath] = useState("/");
  const [showEventsOverlay, setShowEventsOverlay] = useState(false);
  const [recentSuccess, setRecentSuccess] = useState(false);
  const [recentFailed, setRecentFailed] = useState(false);
  const [recentWarning, setRecentWarning] = useState(false);
  const previewRef = useRef(null);
  const badgeTimeoutRef = useRef(null);

  // Get event status from Redux
  const hasActiveEvent = useSelector(builderSelectors.hasActiveProjectEvent);
  const mostRecentEvent = useSelector(builderSelectors.getMostRecentProjectEvent);

  // Track completion events (SUCCESS, FAILED, or SUCCESS with warning) for badge display
  useEffect(() => {
    const status = mostRecentEvent?.status;
    const hasWarning = status === "SUCCESS" && mostRecentEvent?.warningMessage;

    if (status === "SUCCESS" || status === "FAILED") {
      // Clear any existing timeout
      if (badgeTimeoutRef.current) {
        clearTimeout(badgeTimeoutRef.current);
      }

      // Set appropriate state - warning takes precedence over plain success
      setRecentWarning(hasWarning);
      setRecentSuccess(status === "SUCCESS" && !hasWarning);
      setRecentFailed(status === "FAILED");

      // Auto-hide after timeout (3s for success, 5s for failed/warning)
      const timeout = status === "SUCCESS" && !hasWarning ? 3000 : 5000;
      badgeTimeoutRef.current = setTimeout(() => {
        setRecentSuccess(false);
        setRecentFailed(false);
        setRecentWarning(false);
      }, timeout);
    }

    return () => {
      if (badgeTimeoutRef.current) {
        clearTimeout(badgeTimeoutRef.current);
      }
    };
  }, [mostRecentEvent?.id, mostRecentEvent?.status, mostRecentEvent?.warningMessage]);

  // Compute preview base URL using subdomain proxy
  const previewBaseUrl = project?.previewDomain && project?.portFrontend
    ? `http://${project.subdomain || project.uid}.${project.previewDomain}`
    : null;

  // Log when previewBaseUrl changes
  useEffect(() => {
    console.log("[PreviewPanel] previewBaseUrl changed:", previewBaseUrl, "subdomain:", project?.subdomain);
  }, [previewBaseUrl, project?.subdomain]);

  // Handle refresh button click
  const handleRefresh = useCallback(() => {
    if (previewRef.current?.refresh) {
      previewRef.current.refresh();
    }
  }, []);

  // Handle open in new tab
  const handleOpenNewTab = useCallback(() => {
    if (previewBaseUrl) {
      window.open(previewBaseUrl + currentPath, "_blank");
    }
  }, [previewBaseUrl, currentPath]);

  // Handle navigation (Enter key in URL input)
  const handleNavigate = useCallback(() => {
    // Navigation happens automatically via currentPath change
    // Just trigger a refresh to ensure the iframe updates
    if (previewRef.current?.refresh) {
      previewRef.current.refresh();
    }
  }, []);

  // Handle sources refresh button click
  const handleSourcesRefresh = useCallback(() => {
    if (project?.uid && workspaceId) {
      dispatch(loadSourcesTree(workspaceId, project.uid));
    }
  }, [dispatch, workspaceId, project?.uid]);

  return (
    <div className="preview_panel">
      <div className="preview_toolbar">
        <div className="tab_navigation">
          <button
            type="button"
            className={`tab_button ${activeTab === TABS.PREVIEW ? "active" : ""}`}
            onClick={() => setActiveTab(TABS.PREVIEW)}
          >
            <EyeIcon size={14} />
            <Message id="builder_preview_tab" defaultMessage="Preview" />
          </button>
          <button
            type="button"
            className={`tab_button ${activeTab === TABS.SOURCES ? "active" : ""}`}
            onClick={() => setActiveTab(TABS.SOURCES)}
          >
            <CodeXmlIcon size={14} />
            <Message id="builder_sources_tab" defaultMessage="Sources" />
          </button>
          <button
            type="button"
            className={`tab_button ${activeTab === TABS.PUBLISH ? "active" : ""}`}
            onClick={() => setActiveTab(TABS.PUBLISH)}
          >
            <BadgeCheckIcon size={14} />
            <Message id="builder_publish_tab" defaultMessage="Publish" />
          </button>
        </div>

        {/* Sources toolbar - only show when Sources tab is active */}
        {activeTab === TABS.SOURCES && (
          <div className="sources_toolbar">
            <button
              type="button"
              className="nav_button events_button"
              onClick={() => setShowEventsOverlay(true)}
              disabled={!project?.uid}
              title={intl.formatMessage({
                id: "builder_project_events",
                defaultMessage: "Project Events"
              })}
            >
              <ActivityIcon size={16} />
              {/* Badge indicator - priority: running > failed > warning > success */}
              {hasActiveEvent && (
                <span className="event_badge running">
                  <Loader2Icon size={10} className="spinning" />
                </span>
              )}
              {!hasActiveEvent && recentFailed && (
                <span className="event_badge failed">
                  <XIcon size={10} />
                </span>
              )}
              {!hasActiveEvent && !recentFailed && recentWarning && (
                <span className="event_badge warning">
                  <AlertTriangleIcon size={10} />
                </span>
              )}
              {!hasActiveEvent && !recentFailed && !recentWarning && recentSuccess && (
                <span className="event_badge success">
                  <CheckIcon size={10} />
                </span>
              )}
            </button>
            <button
              type="button"
              className="nav_button"
              onClick={handleSourcesRefresh}
              disabled={!project?.portFrontend}
              title={intl.formatMessage({
                id: "builder_sources_refresh",
                defaultMessage: "Refresh files"
              })}
            >
              <RefreshCwIcon size={16} />
            </button>
          </div>
        )}

        {/* Navigation bar - only show when Preview tab is active and URL is available */}
        {activeTab === TABS.PREVIEW && previewBaseUrl && (
          <div className="navigation_bar">
            <input
              type="text"
              className="url_input"
              value={currentPath}
              onChange={(e) => setCurrentPath(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleNavigate()}
              placeholder="/"
            />
            <button
              type="button"
              className="nav_button"
              onClick={handleRefresh}
              title={intl.formatMessage({ id: "builder_preview_refresh", defaultMessage: "Refresh page" })}
            >
              <RefreshCwIcon size={16} />
            </button>
            <button
              type="button"
              className="nav_button"
              onClick={handleOpenNewTab}
              title={intl.formatMessage({ id: "builder_preview_open_new_tab", defaultMessage: "Open preview in a new tab" })}
            >
              <ExternalLinkIcon size={16} />
            </button>
          </div>
        )}
      </div>
      <div className="preview_content_area">
        {activeTab === TABS.PREVIEW && (
          <PreviewContent
            ref={previewRef}
            previewUrl={previewBaseUrl}
            currentPath={currentPath}
            onPathChange={setCurrentPath}
            isAgentRunning={isAgentRunning}
            agentSession={agentSession}
          />
        )}
        {activeTab === TABS.SOURCES && <SourcesViewer projectUid={project?.uid} />}
        {activeTab === TABS.PUBLISH && <PublishContent project={project} />}
      </div>

      {showEventsOverlay && (
        <ProjectEventsOverlay
          projectUid={project?.uid}
          onClose={() => setShowEventsOverlay(false)}
        />
      )}
    </div>
  );
};

export default PreviewPanel;
